import { useCallback, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import { SectionCard } from "@/components/common/SectionCard";
import { ServiceGateBanner } from "./ServiceGateBanner";
import { ServiceRequestApprovalCard } from "./ServiceRequestApprovalCard";
import { ServiceRequestManualEntryPanel } from "./ServiceRequestManualEntryPanel";
import { draftFromServiceRequest } from "@/lib/service/requestApprovalClient";
import { ghostBtnClass, inputClass } from "./serviceShared";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useFilteredServiceRequests } from "./useServiceRequestListFilters";
import { useServiceRequestsPanel } from "./useServiceRequestsPanel";

export function ServiceRequestsPanel() {
  const gate = useServiceAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const m = useServiceRequestsPanel(navTick);
  const [search, setSearch] = useState("");
  const [offeringFilter, setOfferingFilter] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredRequests = useFilteredServiceRequests(
    m.requests,
    m.offerings,
    m.queues,
    search,
    offeringFilter
  );
  const pager = usePagination(filteredRequests);

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
    setExpandedId(null);
  }, [gate]);

  const handleManualSubmitted = useCallback(() => {
    void m.reload();
  }, [m.reload]);

  const toggleExpanded = useCallback((requestId: string) => {
    setExpandedId((cur) => (cur === requestId ? null : requestId));
  }, []);

  const offeringFilterOptions = useMemo(() => {
    const ids = new Set(m.requests.map((r) => r.offering_id).filter(Boolean) as string[]);
    return m.offerings.filter((o) => ids.has(o.id));
  }, [m.requests, m.offerings]);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Inbound requests</h2>
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            Pending queue on the left, manual test intake on the right.
          </p>
        </div>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={handleRefresh}
          disabled={gate.loading || m.listBusy}
        >
          {m.listBusy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <ServiceGateBanner gate={gate} />
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start">
          <div className="min-w-0">
            <SectionCard title="Pending approval">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  className={`${inputClass} sm:flex-1`}
                  placeholder="Search requests…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  className={`${inputClass} sm:w-48`}
                  value={offeringFilter}
                  onChange={(e) => setOfferingFilter(e.target.value)}
                >
                  <option value="">All services</option>
                  {offeringFilterOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <PaginationBar
                pageIndex={pager.pageIndex}
                pageCount={pager.pageCount}
                pageSize={pager.pageSize}
                totalCount={pager.totalCount}
                firstShown={pager.firstShown}
                lastShown={pager.lastShown}
                onPageChange={pager.setPageIndex}
                onPageSizeChange={pager.setPageSize}
                noun="requests"
                alwaysShow
              />

              {m.listBusy && m.requests.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500 dark:text-zinc-400">Loading requests…</p>
              ) : filteredRequests.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500 dark:text-zinc-400">
                  {m.requests.length === 0 ? "No pending requests." : "No requests match your filters."}
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {pager.paginatedItems.map((req) => (
                    <ServiceRequestApprovalCard
                      key={req.id}
                      req={req}
                      queues={m.queues}
                      offerings={m.offerings}
                      expanded={expandedId === req.id}
                      onToggle={() => toggleExpanded(req.id)}
                      draft={m.operationalDrafts[req.id] ?? draftFromServiceRequest(req)}
                      reviewNotes={m.reviewNotes[req.id] ?? ""}
                      actionBusy={m.actionBusyId === req.id}
                      onDraftChange={(patch) => m.setDraft(req.id, patch)}
                      onReviewNotesChange={(value) =>
                        m.setReviewNotes((prev) => ({ ...prev, [req.id]: value }))
                      }
                      onAction={(action) => void m.runAction(req.id, action)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
          <div className="min-w-0">
            <ServiceRequestManualEntryPanel queues={m.queues} onSubmitted={handleManualSubmitted} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
